import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {GeneralContract, Policy} from '../../models/api/product/base.model';
import {Injectable, NgZone} from '@angular/core';
import {Parameter} from '../../models/api/parameter.model';
import {FleetEditActions} from './fleet-edit.actions';
import {RiskDefinition} from '../../models/api/riskDefinition.model';
import {instanceToInstance, instanceToPlain} from 'class-transformer';
import {Person} from '../../models/api/person.model';
import {GeneralContractFLEET, PolicyFLEET} from '../../models/api/product/fleet.model';
import {skipInterceptors} from '../../helpers/request.helper';
import {catchError, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {PolicyService} from '../../services/policy.service';
import {NotificationService} from '../../services/notification.service';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {ValidationActions} from '../../modules/validation/state/validation.actions';
import {NavService} from '../../services/nav.service';
import {RiskFLEET} from '../../models/api/variant/risk/risk-fleet.model';
import {verifyCopiedSubjects} from '../../helpers/policy-copy.helper';
import {Franchise} from '../../models/api/agro/franchise.model';
import {DictionaryItem} from '../../models/api/dictionary.model';
import {FranchiseParameterValue} from '../../models/api/franchise-parameter-value.model';
import {VariantFLEET} from '../../models/api/variant/variant-fleet.model';
import {InstallmentDefinitionTender} from '../../models/api/variant/installment-definition-tender.model';
import {InstallmentDefinition} from '../../models/api/variant/installment-definition.model';
import {
    compareFranchiseAndFranchiseParameter,
    findMatchingInstallment,
    fleetParameterToAttributeDict,
    getFleetRisksDefinition,
} from '../../helpers/fleet.helper';
import {SourcePolicySearchComponent} from '../../modules/shared/components/source-policy-search/source-policy-search.component';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {SourcePolicySearchSearch} from '../../modules/shared/components/source-policy-search/source-policy-search.model';
import {FleetEdit} from '../../modules/registration/modules/fleet-edit/model/fleet-edit.model';
import {clearProperties, clearPropertiesOmitting} from '../../helpers/object.helper';
import ContextType = SourcePolicySearchSearch.ContextType;
import ModelType = SourcePolicySearchSearch.ModelType;
import PolicyType = FleetEdit.PolicyType;
import SetPolicyDataOptions = FleetEditActions.SetPolicyDataOptions;

interface FleetEditStateModel extends ValidationStateModel {
    originalPolicy?: PolicyFLEET;
    generalContract?: GeneralContract;
    activePolicy?: PolicyFLEET;
    vehicleMakeList?: string[];
    riskDefinition?: RiskDefinition;
    calculateResult?: PolicyFLEET;
    isClone?: boolean;
}

@State<FleetEditStateModel>({
    name: FleetEditState.STATE_NAME,
    defaults: {
        generalContract: null,
        originalPolicy: null,
        activePolicy: null,
        validationErrors: null,
        vehicleMakeList: null,
        riskDefinition: null,
        calculateResult: null,
        isClone: null,
    },
})
@Injectable()
export class FleetEditState {
    static readonly STATE_NAME = 'fleetEdit';

    @Selector()
    static generalContract(state: FleetEditStateModel) {
        return state.generalContract;
    }

    @Selector()
    static originalPolicy(state: FleetEditStateModel) {
        return state.originalPolicy;
    }

    @Selector()
    static activePolicy(state: FleetEditStateModel) {
        return state.activePolicy;
    }

    @Selector()
    static premium(state: FleetEditStateModel) {
        return state.calculateResult?.premium;
    }

    @Selector()
    static calculateResult(state: FleetEditStateModel) {
        return state.calculateResult;
    }

    @Selector()
    static vehicleMakeList(state: FleetEditStateModel) {
        return state.vehicleMakeList;
    }

    @Selector()
    static riskDefinition(state: FleetEditStateModel) {
        return state.riskDefinition;
    }

    @Selector()
    static blockSave(state: FleetEditStateModel) {
        return !(typeof state.calculateResult?.premium === 'number');
    }

    @Selector()
    static isUpdating(state: FleetEditStateModel) {
        return !state.isClone && !!state.originalPolicy?.id;
    }

    @Selector()
    static isClone(state: FleetEditStateModel) {
        return state.isClone;
    }

    constructor(
        protected policyService: PolicyService,
        protected notificationService: NotificationService,
        protected navService: NavService,
        protected ngZone: NgZone,
        protected modalService: ModalService,
        protected translate: TranslateService,
        protected store: Store
    ) {}

    @Action(FleetEditActions.SetPolicyData)
    setPolicyData(
        ctx: StateContext<FleetEditStateModel>,
        {originalPolicy = null, generalContract, options}: FleetEditActions.SetPolicyData
    ) {
        const activePolicy = this.mapPolicyData(generalContract, originalPolicy, options);
        const riskDefinition = getFleetRisksDefinition(generalContract);
        if (originalPolicy) {
            this.verifyGCParameters(activePolicy, riskDefinition);
        }
        ctx.patchState({
            originalPolicy,
            generalContract,
            activePolicy,
            riskDefinition,
            calculateResult: !options?.isClone && !!originalPolicy?.id && originalPolicy,
            isClone: options?.isClone ?? false,
        });
    }

    @Action(FleetEditActions.Calculate)
    calculate(ctx: StateContext<FleetEditStateModel>, {policy, productCode}: FleetEditActions.Calculate) {
        return this.policyService
            .calculate(instanceToPlain(policy), productCode, skipInterceptors([409]), PolicyFLEET)
            .pipe(
                tap((res) => {
                    ctx.patchState({
                        calculateResult: res as PolicyFLEET,
                    });
                    ctx.dispatch(new ValidationActions.Clear(FleetEditState.STATE_NAME));
                }),
                catchError((rej: HttpErrorResponse) => {
                    ctx.patchState({calculateResult: null});
                    if (rej.status === 409 && rej.error?.[0]?.defaultMessage) {
                        this.notificationService.error(rej.error?.[0]?.defaultMessage);
                    }
                    return this.handleCalculationError(ctx, rej);
                })
            );
    }

    @Action(FleetEditActions.Save)
    save(ctx: StateContext<FleetEditStateModel>, {policy, productCode}: FleetEditActions.Save) {
        const updating = FleetEditState.isUpdating(ctx.getState());
        const headers = skipInterceptors([409]);
        const body = instanceToPlain(policy);
        const req$ = updating
            ? this.policyService.updatePolicy(body, productCode, headers)
            : this.policyService.savePolicy(body, productCode, headers);
        return req$.pipe(
            tap((res: Policy) => {
                this.notificationService.success(updating ? 'POLICY.UPDATED' : 'POLICY.CREATED');
                this.navService.navigateByCode(NavItemCodes.POLICY_PREVIEW, res?.id);
            }),
            catchError((rej: HttpErrorResponse) => {
                return this.handleCalculationError(ctx, rej);
            })
        );
    }

    @Action(FleetEditActions.SetVehicleMakeList)
    setVehicleMakeList(ctx: StateContext<FleetEditStateModel>, {vehicleMakeList}: FleetEditActions.SetVehicleMakeList) {
        ctx.patchState({vehicleMakeList});
    }

    @Action(FleetEditActions.ResetCalculationResult)
    setBlockSave(ctx: StateContext<FleetEditStateModel>) {
        ctx.patchState({
            calculateResult: null,
        });
    }

    @Action(FleetEditActions.ClearState)
    clearState(ctx: StateContext<FleetEditStateModel>) {
        ctx.setState({
            originalPolicy: null,
            generalContract: null,
            activePolicy: null,
            riskDefinition: null,
            validationErrors: null,
            calculateResult: null,
            isClone: null,
        });
    }

    @Action(FleetEditActions.NonObligatoryExtensionSearchModal)
    nonObligatoryExtensionSearchModal(
        ctx: StateContext<FleetEditStateModel>,
        {generalContractId}: FleetEditActions.NonObligatoryExtensionSearchModal
    ) {
        return this.ngZone.run(
            () =>
                this.modalService.generic({
                    nzTitle: this.translate.instant('FLEET.NON_OBLIGATORY_RISKS_EXTENSION'),
                    nzOkText: this.translate.instant('LABELS.SEARCH'),
                    nzContent: SourcePolicySearchComponent,
                    nzData: {
                        generalContractId,
                        modelType: ModelType.policy,
                        contextType: ContextType.nonObligatoryRisksExtension,
                    } as Partial<SourcePolicySearchComponent>,
                    nzOnOk: async (instance: SourcePolicySearchComponent) => {
                        try {
                            const responseModel = await instance.search().toPromise();
                            // pass policy object to resolver
                            const navExtras = {
                                state: responseModel,
                            };
                            const payload = {
                                generalContractId,
                                policyNumber: instance.policyNumber.value,
                            };
                            this.navService.navigateByCode(
                                NavItemCodes.FLEET_NON_OBLIGATORY_EXTENSION,
                                payload,
                                undefined,
                                navExtras
                            );
                            return true;
                        } catch (e) {
                            return false;
                        }
                    },
                }).afterClose
        );
    }

    @Action(FleetEditActions.GreenCardExtensionSearchModal)
    greenCardExtensionSearchModal(
        ctx: StateContext<FleetEditStateModel>,
        {generalContractId}: FleetEditActions.NonObligatoryExtensionSearchModal
    ) {
        return this.ngZone.run(
            () =>
                this.modalService.generic({
                    nzTitle: this.translate.instant('NAV.POLICY_ADD_GREEN_CARD.LABEL'),
                    nzOkText: this.translate.instant('LABELS.SEARCH'),
                    nzContent: SourcePolicySearchComponent,
                    nzData: {
                        generalContractId,
                        modelType: ModelType.policy,
                        contextType: ContextType.greenCardAdd,
                    } as Partial<SourcePolicySearchComponent>,
                    nzOnOk: async (instance: SourcePolicySearchComponent) => {
                        try {
                            const responseModel = await instance.search().toPromise();
                            // pass policy object to resolver
                            const navExtras = {
                                state: responseModel,
                            };
                            const payload = {
                                generalContractId,
                                policyNumber: instance.policyNumber.value,
                            };
                            this.navService.navigateByCode(
                                NavItemCodes.FLEET_GREEN_CARD_EXTENSION,
                                payload,
                                undefined,
                                navExtras
                            );
                            return true;
                        } catch (e) {
                            return false;
                        }
                    },
                }).afterClose
        );
    }

    protected mapPolicyData(
        generalContract: GeneralContractFLEET,
        originalPolicy: PolicyFLEET,
        options: SetPolicyDataOptions = {}
    ): PolicyFLEET {
        if (options.isClone) {
            return this.prepareClone(generalContract, originalPolicy, options);
        } else if (this.isNonObligatoryExtension(originalPolicy)) {
            return this.prepareNonObligatoryExtension(generalContract, originalPolicy, options);
        } else if (this.isGreenCardExtension(originalPolicy)) {
            return this.prepareGreenCardExtension(generalContract, originalPolicy);
        } else if (originalPolicy) {
            return this.copyOriginalPolicy(generalContract, originalPolicy);
        } else {
            return this.mapNewPolicyData(generalContract);
        }
    }

    protected mapNewPolicyData(generalContract: GeneralContractFLEET): PolicyFLEET {
        const policy = new PolicyFLEET();
        policy.generalContract = instanceToInstance(generalContract, {ignoreDecorators: true});
        if (policy.generalContract?.insurer) {
            policy.insurer = policy.generalContract.insurer;
        } else if (!policy.insurer) {
            policy.insurer = new Person();
        }
        policy.variant = instanceToInstance(generalContract.variants?.[0], {
            ignoreDecorators: true,
        });
        return policy;
    }

    protected copyOriginalPolicy(
        generalContract: Readonly<GeneralContractFLEET>,
        originalPolicy: PolicyFLEET
    ): PolicyFLEET {
        const policyCopy: PolicyFLEET = instanceToInstance(originalPolicy, {ignoreDecorators: true});
        const gcVariant = generalContract.variants?.[0];
        this.verifyInstallments(policyCopy, gcVariant);
        const gcRisks = instanceToInstance(gcVariant.risks, {ignoreDecorators: true});
        policyCopy.variant.risks = this.mergeExistingRisks(gcRisks, policyCopy.variant.risks);
        if (generalContract?.insurer) {
            policyCopy.insurer = instanceToInstance(generalContract.insurer, {ignoreDecorators: true});
        }
        verifyCopiedSubjects(policyCopy, generalContract);
        return policyCopy;
    }

    /**
     * search if original calculation has risks defined in GC
     */
    protected mergeExistingRisks(gcRisks: RiskFLEET[], originalRisks: RiskFLEET[]): RiskFLEET[] {
        return gcRisks.map((gcRisk) => {
            const originalRisk = originalRisks.find((item) => item.code === gcRisk.code);
            if (originalRisk) {
                originalRisk.selected = true;
                return originalRisk;
            } else {
                return gcRisk;
            }
        });
    }

    protected handleCalculationError(ctx: StateContext<FleetEditStateModel>, rej: HttpErrorResponse): Observable<null> {
        ctx.dispatch(new ValidationActions.Set(FleetEditState.STATE_NAME, rej.error));
        return of(null);
    }

    /**
     * CEVO-1509 - remove risk insurance sum and variant params which are not listed in gc parameters
     * @param activePolicy
     * @param riskDefinition
     * @protected
     */
    protected verifyGCParameters(activePolicy: PolicyFLEET, riskDefinition: RiskDefinition) {
        for (const riskDef of riskDefinition.risks) {
            if (riskDef.insuranceSumFromDomain) {
                const policyRisk = activePolicy.variant.risks.find((risk) => risk.code === riskDef.risk.code);
                // check insurance sum is defined in domain
                if (
                    policyRisk &&
                    !riskDef.insuranceSumDomain.some((param) => param.value === policyRisk.insuranceSum)
                ) {
                    policyRisk.insuranceSum = null;
                }
            }
        }
        for (const paramDef of riskDefinition.parameters) {
            const paramAttrCode = fleetParameterToAttributeDict[paramDef.parameterCode];
            this.verifyGCVariantParameter(paramDef, activePolicy, paramAttrCode);
        }
    }

    /**
     * CEVO-1509
     * Reset variant params which are not listed in gc parameters
     * for now since only franchise parameters are dynamic, only value type Franchise case should occur,
     * but added DictionaryItem for safety/future
     * @param param
     * @param activePolicy
     * @param paramAttrCode
     * @protected
     */
    protected verifyGCVariantParameter(param: Parameter, activePolicy: PolicyFLEET, paramAttrCode?: string) {
        if (paramAttrCode && activePolicy.variant.params[paramAttrCode]) {
            const value: DictionaryItem | Franchise = activePolicy.variant.params[paramAttrCode];
            if (value instanceof Franchise) {
                const values = param.values as FranchiseParameterValue[];
                if (!values.some((paramValue) => compareFranchiseAndFranchiseParameter(paramValue, value))) {
                    activePolicy.variant.params[paramAttrCode] = null;
                }
            } else if (value instanceof DictionaryItem) {
                const values = param.values as DictionaryItem[];
                if (!values.some((paramValue) => paramValue.code !== value.code)) {
                    activePolicy.variant.params[paramAttrCode] = null;
                }
            }
        }
    }

    protected verifyInstallments(policy: PolicyFLEET, gcVariant: VariantFLEET) {
        const selectedInstallment =
            policy.variant.installments?.find((item) => item.selected) ??
            policy.variant.tenderInstallments?.find((item) => item.selected);
        // filter installments available to user
        policy.variant.installments = instanceToInstance(gcVariant.installments, {ignoreDecorators: true})
            .filter((item) => item.selected)
            .map(this.deselectInstallment);
        // all tender installments are available to user
        policy.variant.tenderInstallments = instanceToInstance(gcVariant.tenderInstallments, {
            ignoreDecorators: true,
        }).map(this.deselectInstallment);
        // select previously selected installment
        if (selectedInstallment) {
            const list =
                selectedInstallment instanceof InstallmentDefinitionTender
                    ? policy.variant.tenderInstallments
                    : policy.variant.installments;
            const installmentToSelect = findMatchingInstallment(list, selectedInstallment);
            if (installmentToSelect) {
                installmentToSelect.selected = true;
            }
        }
    }

    protected deselectInstallment = <T extends InstallmentDefinition>(installment: T) => {
        installment.selected = false;
        return installment;
    };

    protected isNonObligatoryExtension = (originalPolicy?: PolicyFLEET) =>
        originalPolicy?.variant?.params?.type?.code === PolicyType.NON_OBLIGATORY_RISKS_EXTENSION;

    protected isGreenCardExtension = (originalPolicy?: PolicyFLEET) =>
        originalPolicy?.variant?.params?.type?.code === PolicyType.ZK_EXTENSION;

    /**
     * mutates risks active policy risks based on extensionExtraData
     * selects currency dictionary directly from store for brevity
     */
    protected prepareNonObligatoryExtension(
        generalContract: Readonly<GeneralContractFLEET>,
        originalPolicy: PolicyFLEET,
        options: SetPolicyDataOptions
    ) {
        const policy = this.copyOriginalPolicy(generalContract, originalPolicy);
        this.prepareNonObligatoryRisks(policy, options);
        // copy installments without changes
        policy.variant.installments = instanceToInstance(originalPolicy.variant.installments, {ignoreDecorators: true});
        return policy;
    }

    /**
     * Mutates passed policy
     */
    protected prepareNonObligatoryRisks(policy: PolicyFLEET, options: SetPolicyDataOptions) {
        if (policy.extensionExtraData?.risksWithInsuranceSums?.length) {
            for (const {riskCode, currencyCode, insuranceSum} of policy.extensionExtraData.risksWithInsuranceSums) {
                const risk = policy.variant.risks.find((item) => item.code === riskCode);
                if (risk) {
                    risk.selected = true;
                    risk.insuranceSum = insuranceSum;
                    risk.insuranceSumCurrency = options.currencies.find((item) => item.code === currencyCode);
                }
            }
        }
    }

    protected prepareGreenCardExtension(generalContract: Readonly<GeneralContractFLEET>, originalPolicy: PolicyFLEET) {
        const policy = this.copyOriginalPolicy(generalContract, originalPolicy);
        policy.variant.installments = instanceToInstance(originalPolicy.variant.installments, {ignoreDecorators: true});
        return policy;
    }

    protected prepareClone(
        generalContract: Readonly<GeneralContractFLEET>,
        originalPolicy: PolicyFLEET,
        options: SetPolicyDataOptions
    ): PolicyFLEET {
        const policy = this.copyOriginalPolicy(generalContract, originalPolicy);
        if (policy.variant.params.type?.code === PolicyType.NON_OBLIGATORY_RISKS_EXTENSION) {
            this.prepareNonObligatoryRisks(policy, options);
        }
        policy.clauses = policy.clauses.filter((clause) => !clause.doNotShowOnGui);
        policy.variant.params.type = null;
        clearPropertiesOmitting(policy, [
            'insurer',
            'insureds',
            'generalContract',
            'installments',
            'variant',
            'vehicle',
            'vehicleUser',
            'vehicleCoOwner',
            'start',
            'stop',
            'conclusion',
            'clauses',
            'isTransfer',
            'transferee',
        ]);
        clearProperties(policy.variant.risks, ['policyNumber']);
        return policy;
    }
}
